<template>
    <page-controls>
        <template v-slot:left>
            <div class="@3xl/controls:w-1/2 @4xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="toggleFilterable">
                    <template v-slot:placeholder>
                        <span class="mr-2">Search</span>
                        <vx-badge v-if="filtersCount > 0" :borders="false" size="xs" @click.stop="reset">
                            {{ pluralized('filter', filtersCount, true) }}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply" class="space-y-4">
                            <div>
                                <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                    Keyword Search
                                </filter-label>

                                <vx-input
                                    type="text"
                                    data-test="filters-query"
                                    placeholder="Search"
                                    maxlength="191"
                                    size="lg"
                                    v-model="filters.query"
                                >
                                    <template v-slot:right>
                                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                    </template>
                                </vx-input>
                            </div>
                            <div>
                                <filter-label
                                    :canReset="!!filters.iso_country"
                                    @reset="filters.iso_country = undefined"
                                >
                                    Country
                                </filter-label>

                                <vx-selectable
                                    data-test="filters-country"
                                    v-model="filters.iso_country"
                                    :reduce="(option) => option.value"
                                    :clearable="false"
                                    size="lg"
                                    :options="[
                                        {
                                            label: '🇺🇸 US',
                                            value: 'US',
                                        },
                                        {
                                            label: '🇨🇦 CA',
                                            value: 'CA',
                                        },
                                    ]"
                                >
                                </vx-selectable>
                            </div>
                            <div>
                                <filter-label
                                    :canReset="!!filters.number_type"
                                    @reset="filters.number_type = undefined"
                                >
                                    Number Type
                                </filter-label>

                                <vx-selectable
                                    data-test="filters-number-type"
                                    v-model="filters.number_type"
                                    :reduce="(option) => option.value"
                                    :clearable="false"
                                    size="lg"
                                    :options="getNumberTypeOptions()"
                                >
                                </vx-selectable>
                            </div>

                            <div v-if="props.contactGroups">
                                <filter-label
                                    :canReset="filters.is_corporate !== undefined"
                                    @reset="
                                        () => {
                                            filters.is_corporate = undefined;
                                        }
                                    "
                                >
                                    <span :id="groupAssignmentLabelId">Group Assignment</span>
                                </filter-label>

                                <div role="group" :aria-labelledby="groupAssignmentLabelId" class="flex flex-col gap-2">
                                    <vx-checkbox-selection-item
                                        @selected="
                                            corporateFilter = {
                                                ...corporateFilter,
                                                corporate: !corporateFilter.corporate,
                                            }
                                        "
                                        :active="corporateFilter.corporate"
                                    >
                                        Assigned
                                    </vx-checkbox-selection-item>
                                    <vx-checkbox-selection-item
                                        @selected="
                                            corporateFilter = {
                                                ...corporateFilter,
                                                notCorporate: !corporateFilter.notCorporate,
                                            }
                                        "
                                        :active="corporateFilter.notCorporate"
                                    >
                                        Not Assigned
                                    </vx-checkbox-selection-item>
                                </div>
                            </div>

                            <div>
                                <filter-label
                                    :canReset="filters.use_for_sending !== undefined"
                                    @reset="
                                        () => {
                                            filters.use_for_sending = undefined;
                                        }
                                    "
                                >
                                    <span :id="sendingLabelId">Sending</span>
                                </filter-label>

                                <div role="group" :aria-labelledby="sendingLabelId" class="flex flex-col gap-2">
                                    <vx-checkbox-selection-item
                                        @selected="
                                            sendingFilter = { ...sendingFilter, sending: !sendingFilter.sending }
                                        "
                                        :active="sendingFilter.sending"
                                    >
                                        Used for Sending
                                    </vx-checkbox-selection-item>
                                    <vx-checkbox-selection-item
                                        @selected="
                                            sendingFilter = { ...sendingFilter, notSending: !sendingFilter.notSending }
                                        "
                                        :active="sendingFilter.notSending"
                                    >
                                        Not used for Sending
                                    </vx-checkbox-selection-item>
                                </div>
                            </div>

                            <div>
                                <filter-label
                                    :canReset="filters.in_pool !== undefined"
                                    @reset="
                                        () => {
                                            filters.in_pool = undefined;
                                        }
                                    "
                                >
                                    <span :id="phonePoolLabelId">Phone Pool</span>
                                </filter-label>

                                <div role="group" :aria-labelledby="phonePoolLabelId" class="flex flex-col gap-2">
                                    <vx-checkbox-selection-item
                                        @selected="poolFilter = { ...poolFilter, inPool: !poolFilter.inPool }"
                                        :active="poolFilter.inPool"
                                    >
                                        In Pool
                                    </vx-checkbox-selection-item>
                                    <vx-checkbox-selection-item
                                        @selected="poolFilter = { ...poolFilter, notInPool: !poolFilter.notInPool }"
                                        :active="poolFilter.notInPool"
                                    >
                                        Not in Pool
                                    </vx-checkbox-selection-item>
                                </div>
                            </div>

                            <div class="flex gap-2 pt-4">
                                <vx-button type="button" size="lg" color="muted-light" @click="cancel"
                                    >Cancel</vx-button
                                >
                                <vx-button data-test="filters-apply" size="lg" type="submit" block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="@3xl/controls:w-1/2 @4xl/controls:w-56">
                <vx-button-group size="lg" class="w-full">
                    <vx-select
                        :modelValue="props.modelValue.sort_field"
                        @update:modelValue="
                            updateModelValue('sort_field', $event);
                            emit('apply');
                        "
                        :options="[
                            {
                                value: 'id',
                                label: 'Sort: Created',
                            },
                            {
                                value: 'phone_number',
                                label: 'Sort: Number',
                            },
                        ]"
                    ></vx-select>
                    <vx-button
                        @click.prevent="
                            props.modelValue.sort_dir === 'asc'
                                ? updateModelValue('sort_dir', 'desc')
                                : updateModelValue('sort_dir', 'asc');
                            emit('apply');
                        "
                        type="button"
                        color="white"
                        shadow
                        data-test="sort-direction"
                        :aria-label="`Sort ${
                            props.modelValue.sort_dir === 'asc' ? 'ascending' : 'descending'
                        }. Press to toggle sort direction.`"
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="props.modelValue.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </div>
        </template>

        <template v-slot:right>
            <slot name="right"></slot>
        </template>
    </page-controls>
</template>

<script setup>
import PageControls from '~/components/general/PageControls.vue';
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faCircleCheck,
    faMagnifyingGlass,
    faCircleXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxButtonGroup,
    VxFilterable,
    VxInput,
    VxSelect,
    VxSelectable,
    VxCheckboxSelectionItem,
} from '@voxie/frontend-components';
import { computed, nextTick, ref, useId, watch } from 'vue';
import { getNumberTypeOptions } from '~/constants/phoneNumbers';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';

const filters = ref({});
const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    contactGroups: {
        type: Boolean,
        default: false,
    },
});

const groupAssignmentLabelId = useId();
const corporateFilter = computed({
    get() {
        const value = filters.value.is_corporate;

        return {
            corporate: value === 1 || value === null,
            notCorporate: value === 0 || value === null,
        };
    },
    set({ corporate, notCorporate }) {
        if (corporate && !notCorporate) {
            filters.value.is_corporate = 1;
        } else if (!corporate && notCorporate) {
            filters.value.is_corporate = 0;
        } else if (corporate && notCorporate) {
            filters.value.is_corporate = null;
        } else {
            filters.value.is_corporate = undefined;
        }
    },
});

const sendingLabelId = useId();
const sendingFilter = computed({
    get() {
        const value = filters.value.use_for_sending;

        return {
            sending: value === 1 || value === null,
            notSending: value === 0 || value === null,
        };
    },
    set({ sending, notSending }) {
        if (sending && !notSending) {
            filters.value.use_for_sending = 1;
        } else if (!sending && notSending) {
            filters.value.use_for_sending = 0;
        } else if (sending && notSending) {
            filters.value.use_for_sending = null;
        } else {
            filters.value.use_for_sending = undefined;
        }
    },
});

const phonePoolLabelId = useId();
const poolFilter = computed({
    get() {
        const value = filters.value.in_pool;

        return {
            inPool: value === 1 || value === null,
            notInPool: value === 0 || value === null,
        };
    },
    set({ inPool, notInPool }) {
        if (inPool && !notInPool) {
            filters.value.in_pool = 1;
        } else if (!inPool && notInPool) {
            filters.value.in_pool = 0;
        } else if (inPool && notInPool) {
            filters.value.in_pool = null;
        } else {
            filters.value.in_pool = undefined;
        }
    },
});

watch(
    () => props.modelValue,
    (modelValue) => {
        filters.value = { ...modelValue };
    },
    {
        deep: true,
        immediate: true,
    }
);

const emit = defineEmits(['update:modelValue', 'apply', 'reset']);

const active = ref(false);

const toggleFilterable = (event) => {
    active.value = event;
};

const filtersCount = computed(
    () =>
        (props.modelValue.query ? 1 : 0) +
        (props.modelValue.iso_country ? 1 : 0) +
        (props.modelValue.number_type ? 1 : 0) +
        ([null, 1, 0].includes(props.modelValue.use_for_sending) ? 1 : 0) +
        ([null, 1, 0].includes(props.modelValue.in_pool) ? 1 : 0) +
        ([null, 1, 0].includes(props.modelValue.is_corporate) ? 1 : 0)
);

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = async () => {
    filters.value = { ...props.modelValue };
    active.value = false;
};

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};

const updateModelValue = (key, value) => {
    emit('update:modelValue', {
        ...props.modelValue,
        [key]: value,
    });
};
</script>
